import React, { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBed, FaBath, FaCar, FaRulerCombined } from 'react-icons/fa';
import { getImageUrl } from '../utils/api';
import { formatAreaDisplay } from '../utils/formatMeasurement';
import { formatDisplayPrice } from '../utils/formatPrice';

// memo — prevents re-renders from the 60 s silent background refresh in Home
const PropertyCard = memo(({ property, section = 'properties', fromCategory = null }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getPropertyDetails = () => {
    const type = property.category || property.type;

    if (type === 'Agricultural Land' || type === 'Farmhouse' || type === 'Open Plot') {
      return (
        <div className="flex items-center gap-2 text-sm text-textGray">
          <FaRulerCombined />
          <span>{formatAreaDisplay(property)}</span>
        </div>
      );
    }

    if (type === 'Office / Commercial Space') {
      return (
        <div className="flex justify-between text-sm text-textGray">
          <div className="flex items-center gap-1"><FaRulerCombined /><span>{property.area || 'N/A'} SFT</span></div>
          <div className="flex items-center gap-1"><FaCar /><span>{property.parking || 'N/A'}</span></div>
        </div>
      );
    }

    return (
      <div className="flex justify-between text-sm text-textGray">
        {property.bedrooms  && <div className="flex items-center gap-1"><FaBed /><span>{property.bedrooms}</span></div>}
        {property.bathrooms && <div className="flex items-center gap-1"><FaBath /><span>{property.bathrooms}</span></div>}
        {property.area      && <div className="flex items-center gap-1"><FaRulerCombined /><span>{property.area} SFT</span></div>}
      </div>
    );
  };

  const handleViewDetails = () => {
    const propertyId = property._id || property.id;
    const isCategory = section === 'category';
    const isChoice   = section === 'choice';

    // Save scroll target — both website back button and browser back button read this.
    // For the 'choice' section, the choice tile's restoreScrollId (e.g. "choice-agricultural-land")
    // was already written by ChoiceProperties when the tile was clicked. We must NOT overwrite it
    // with the raw property ID here, because Home has no ref for a raw ID in the choice section.
    // ChoiceCategoryProperties.handleBackClick will re-write it correctly before navigate(-1).
    try {
      if (isChoice) {
        sessionStorage.setItem('restoreScrollSection', 'choice');
      } else {
        sessionStorage.setItem('restoreScrollId', propertyId);
        sessionStorage.setItem('restoreScrollSection', section);
      }
      const detailContext = {
        propertyId,
        fromRoute: `${location.pathname}${location.search}`,
        clickedPropertyId: propertyId,
        section,
        isCategory,
        isChoice,
        fromCategory,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('lastClickedProperty', JSON.stringify(detailContext));
      sessionStorage.setItem('propertyDetailsContext', JSON.stringify(detailContext));
    } catch (_) {}

    navigate(`/property/${propertyId}`, {
      state: {
        fromRoute:         `${location.pathname}${location.search}`,
        clickedPropertyId: propertyId,
        fromCategory,
        section,
        isCategory,
        isChoice,
      },
    });
  };

  const propertyCode = property.propertyCode;
  const cardCategory = property.category || property.type;
  const sectionBadge = property.sections?.includes('featured') ? 'Featured' : property.sections?.includes('highlights') ? 'Highlights' : property.sections?.includes('choice') ? 'Choice' : property.sections?.includes('user-submitted') ? 'Published' : null;
  const showTopOverlayMeta = false;

  return (
    <div className="bg-secondary/10 border-2 border-secondary hover:border-primary rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
      <div className="relative">
        <img
          src={getImageUrl(property.images?.[0] || property.image)}
          alt={property.title}
          className="w-full h-48 object-cover"
          loading="lazy"
        />

        {(property.propertyCode || (showTopOverlayMeta && (cardCategory || sectionBadge))) && (
          <div className="absolute inset-x-0 top-0 z-20 flex flex-col gap-2 p-3 bg-transparent text-white">
            <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide">
              <span className="text-white px-3 py-1 rounded-md" style={{ backgroundColor: '#EA580C' }}>{property.propertyCode ? `ID: ${property.propertyCode}` : 'Property'}</span>
              {showTopOverlayMeta && sectionBadge && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.08em] text-white">
                  {sectionBadge}
                </span>
              )}
            </div>
            {showTopOverlayMeta && cardCategory && (
              <div className="text-sm font-semibold text-white">{cardCategory}</div>
            )}
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-bold text-textDark">{property.title}</h3>
          {property.propertyCode && (
            <span className="text-xs text-blue-600 font-semibold bg-blue-50 px-2 py-1 rounded">
              {property.propertyCode}
            </span>
          )}
        </div>
        <p className="text-textGray text-sm mb-2">{property.location}</p>
        <p className="text-xl font-bold text-textDark mb-3">{formatDisplayPrice(property.price)}</p>
        <div className="mb-4 flex-grow">{getPropertyDetails()}</div>
        <button
          onClick={handleViewDetails}
          className="w-full bg-secondary text-black font-semibold py-2 rounded border-2 border-black hover:bg-secondary/80 transition"
        >
          View Details
        </button>
      </div>
    </div>
  );
});

PropertyCard.displayName = 'PropertyCard';
export default PropertyCard;
